import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { isWeb } from '../util/platform';
import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppButton from '../components/AppButton';
import Util from '../util/util.js';
import { useFormValidation, validateRequired } from '../util/validation';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { t as translate, useTranslation } from '../i18n';

// Helper to format date for input
const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to parse date from input (as local time at noon to avoid timezone day-shift)
const parseDateFromInput = (dateStr) => {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  // Create date at noon local time to avoid timezone issues
  const parsed = new Date(year, month - 1, day, 12, 0, 0);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const validateStartDate = (value) =>
  parseDateFromInput(value) ? null : translate('drawings:create.invalidStartDate');

// Function validator (not array) so it receives all form values for the cross-field check
const validateEndDate = (value, values) => {
  const endDate = parseDateFromInput(value);
  if (!endDate) return translate('drawings:create.invalidEndDate');
  const startDate = parseDateFromInput(values.startDateStr);
  if (startDate && startDate >= endDate) return translate('drawings:create.endBeforeStart');
  return null;
};

const CreateDrawing = ({ navigation, route }) => {
  const { t } = useTranslation('drawings');

  const { containerStyle } = useResponsiveStyles();
  const { organization, admin, userAdmin, orgAdmin } = route.params || {};

  const [loading, setLoading] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [availableOrgs, setAvailableOrgs] = useState([]);

  // Form state
  const { values, setFieldValue, setDisplayErrors, handleChange, validate, getFieldError } =
    useFormValidation(
      {
        name: '',
        description: '',
        selectedOrgs: [],
        startDateStr: formatDateForInput(new Date()),
        endDateStr: formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
        prizeName: '',
        prizeValue: '',
        prizeDescription: '',
      },
      {
        name: [(v) => validateRequired(v, t('create.nameField'))],
        selectedOrgs: [(v) => (v.length === 0 ? t('create.selectOrg') : null)],
        startDateStr: [validateStartDate],
        endDateStr: validateEndDate,
      },
    );
  const {
    name,
    description,
    selectedOrgs,
    startDateStr,
    endDateStr,
    prizeName,
    prizeValue,
    prizeDescription,
  } = values;

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setOrgsLoading(true);
      // Get all organizations
      const response = await Util.get('Resty', '/items/organizations/all');
      const orgs = response?.items?.map((item) => item.organization) || [];
      setAvailableOrgs(orgs);
    } catch (error) {
      console.log('Error loading organizations:', error);
      setAvailableOrgs([]);
    } finally {
      setOrgsLoading(false);
    }
  };

  const toggleOrg = (org) => {
    setFieldValue(
      'selectedOrgs',
      selectedOrgs.includes(org) ? selectedOrgs.filter((o) => o !== org) : [...selectedOrgs, org],
    );
  };

  const showMessage = (title, message, onOk) => {
    if (isWeb()) {
      window.alert(`${title}\n\n${message}`);
      if (onOk) onOk();
    } else {
      Alert.alert(title, message, [{ text: 'OK', onPress: onOk }]);
    }
  };

  const handleSubmit = async () => {
    setDisplayErrors(true);
    if (!validate().isValid) return;

    const startDate = parseDateFromInput(startDateStr);
    const endDate = parseDateFromInput(endDateStr);

    try {
      setLoading(true);
      const response = await Util.post('Resty', '/items/drawings', {
        body: {
          name: name.trim(),
          description: description.trim(),
          organizations: selectedOrgs,
          startDate: startDate.getTime(),
          endDate: endDate.getTime(),
          prizeInfo: {
            name: prizeName.trim(),
            value: prizeValue.trim(),
            description: prizeDescription.trim(),
          },
        },
      });

      if (response && !response.hasError) {
        showMessage(t('create.successTitle'), t('create.created'), () => navigation.goBack());
      } else {
        showMessage('Error', response.error || t('create.createFailed'));
      }
    } catch (error) {
      console.log('Error creating drawing:', error);
      showMessage('Error', t('create.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} type="pop" />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        <CustomText style={styles.pageTitle}>{t('create.title')}</CustomText>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.nameLabelRequired')}</CustomText>
          <TextInput
            style={[styles.input, getFieldError('name') && styles.inputError]}
            value={name}
            onChangeText={handleChange('name')}
            placeholder={t('create.namePlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
          />
          {getFieldError('name') && (
            <CustomText style={styles.fieldError}>{getFieldError('name')}</CustomText>
          )}
        </View>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.description')}</CustomText>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={description}
            onChangeText={handleChange('description')}
            placeholder={t('create.descriptionPlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
            multiline
            numberOfLines={3}
          />
        </View>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.organizationsLabelRequired')}</CustomText>
          <CustomText style={styles.hint}>
            {t('create.orgHint')}
          </CustomText>
          <View style={styles.orgsContainer}>
            {orgsLoading ? (
              <CustomText style={styles.loadingText}>{t('create.loadingOrgs')}</CustomText>
            ) : (
              availableOrgs.map((org) => (
                <TouchableOpacity
                  key={org}
                  style={[
                    styles.orgChip,
                    selectedOrgs.includes(org) && styles.orgChipSelected,
                  ]}
                  onPress={() => toggleOrg(org)}
                >
                  <CustomText
                    style={[
                      styles.orgChipText,
                      selectedOrgs.includes(org) && styles.orgChipTextSelected,
                    ]}
                  >
                    {org}
                  </CustomText>
                </TouchableOpacity>
              ))
            )}
          </View>
          {getFieldError('selectedOrgs') && (
            <CustomText style={styles.fieldError}>{getFieldError('selectedOrgs')}</CustomText>
          )}
        </View>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.startDateLabelRequired')}</CustomText>
          <CustomText style={styles.hint}>{t('create.dateFormatHint')}</CustomText>
          <TextInput
            style={[styles.input, getFieldError('startDateStr') && styles.inputError]}
            value={startDateStr}
            onChangeText={handleChange('startDateStr')}
            placeholder={t('create.startDatePlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
          />
          {getFieldError('startDateStr') && (
            <CustomText style={styles.fieldError}>{getFieldError('startDateStr')}</CustomText>
          )}
        </View>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.endDateLabelRequired')}</CustomText>
          <CustomText style={styles.hint}>{t('create.dateFormatHint')}</CustomText>
          <TextInput
            style={[styles.input, getFieldError('endDateStr') && styles.inputError]}
            value={endDateStr}
            onChangeText={handleChange('endDateStr')}
            placeholder={t('create.endDatePlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
          />
          {getFieldError('endDateStr') && (
            <CustomText style={styles.fieldError}>{getFieldError('endDateStr')}</CustomText>
          )}
        </View>

        <CustomText style={styles.sectionHeader}>{t('create.prizeInformation')}</CustomText>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.prizeName')}</CustomText>
          <TextInput
            style={styles.input}
            value={prizeName}
            onChangeText={handleChange('prizeName')}
            placeholder={t('create.prizeNamePlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
          />
        </View>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.prizeValue')}</CustomText>
          <TextInput
            style={styles.input}
            value={prizeValue}
            onChangeText={handleChange('prizeValue')}
            placeholder={t('create.prizeValuePlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
          />
        </View>

        <View style={styles.section}>
          <CustomText style={styles.label}>{t('create.prizeDescription')}</CustomText>
          <CustomText style={styles.hint}>
            {t('create.claimLinkHint')}
          </CustomText>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={prizeDescription}
            onChangeText={handleChange('prizeDescription')}
            placeholder={t('create.prizeDescriptionPlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textSecondary}
            multiline
            numberOfLines={2}
          />
        </View>

        <AppButton
          title={loading ? t('create.creating') : t('create.submit')}
          onPress={handleSubmit}
          isEnabled={!loading}
          buttonStyle={styles.submitButton}
        />
      </ScrollView>
      <Footer organization={organization} admin={admin} userAdmin={userAdmin} orgAdmin={orgAdmin} />
    </View>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.inputBackground,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  content: {
    padding: 20,
    paddingBottom: 100,
    flexGrow: 1,
    alignItems: 'stretch',
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: AppStyles.colorSet.textPrimary,
    marginBottom: 24,
  },
  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: AppStyles.colorSet.textPrimary,
    marginTop: 20,
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: AppStyles.colorSet.textPrimary,
    marginBottom: 8,
  },
  hint: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    marginBottom: 8,
  },
  input: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AppStyles.borderSet.default,
    padding: 12,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
  inputError: {
    borderColor: AppStyles.colorSet.error,
  },
  fieldError: {
    marginTop: 6,
    fontSize: 13,
    color: AppStyles.colorSet.error,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  orgsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  orgChip: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderWidth: 1,
    borderColor: AppStyles.borderSet.default,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 8,
  },
  orgChipSelected: {
    backgroundColor: AppStyles.colorSet.success,
    borderColor: AppStyles.colorSet.success,
  },
  orgChipText: {
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },
  orgChipTextSelected: {
    color: AppStyles.colorSet.whiteColor,
  },
  loadingText: {
    fontSize: 14,
    color: AppStyles.colorSet.textSecondary,
    fontStyle: 'italic',
  },
  dateButton: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AppStyles.borderSet.default,
    padding: 12,
  },
  dateButtonText: {
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
  submitButton: {
    marginTop: 24,
    width: '100%',
  },
};

export default CreateDrawing;
